import { useCallback } from "react";
import jschardet from "jschardet";

type ConvertedFile = {
  file: File;
  detectedEncoding: string;
  confidence: number;
};

const useFileEncodingConversion = () => {
  /**
   * Converts a file to UTF-8 encoding.
   * If the file is already UTF-8, it is returned as is.
   */
  const convertFileToUTF8 = useCallback(async (file: File): Promise<ConvertedFile> => {
    const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

    const uint8Array = new Uint8Array(arrayBuffer);
    try {
      const utf8Decoder = new TextDecoder("utf-8", { fatal: true });
      utf8Decoder.decode(uint8Array);
      // File is valid UTF-8; return it as is.
      return { file, detectedEncoding: "utf-8", confidence: 1 };
    } catch {
      // Decoding failed, so the file is not valid UTF-8.
      // Proceed with detection and conversion.
    }

    // Use a sample for encoding detection (e.g., first 10k bytes)
    const sample = uint8Array.slice(0, 10000);
    const detection = jschardet.detect(sample.toString());

    const detectedEncoding = detection.encoding || "utf-8";
    const confidence = detection.confidence || 0;

    // Decode the file content using the detected encoding.
    let decodedText: string;
    try {
      const decoder = new TextDecoder(detectedEncoding);
      decodedText = decoder.decode(uint8Array);
    } catch {
      // Fallback to ISO-8859-1 if the detected encoding is not supported
      const decoder = new TextDecoder("iso-8859-1");
      decodedText = decoder.decode(uint8Array);
    }

    // Re-encode the text as UTF-8
    const encoder = new TextEncoder(); // Defaults to UTF-8
    const utf8Buffer = encoder.encode(decodedText);
    const utf8File = new File([utf8Buffer], file.name, { type: file.type });

    return { file: utf8File, detectedEncoding, confidence };
  }, []);

  return { convertFileToUTF8 };
};

export default useFileEncodingConversion;
